/* The v13 reconcile card (locked 2026-08-31) — spec of record:
 * mock-ov-reco-compare.html (bottom sections) + mock-commitment-diagonal
 * §4.
 *
 * Violet. Text identity line (NO chart). The cause is a violet sentence
 * WITH THE DATES — the alert popup is dead. The ruled movements/balance
 * table stays: Σ row, ✗ on the disagreeing balance, source chips per
 * figure; canon icons (➜ movements, amber dot balance); NEW canon — the
 * balance line gets the same bold+highlight treatment as the Σ row so
 * the two compared figures stand out together. ONE proposal, engine-
 * picked by recency — no fix-choice buttons ever. Commitment breaks
 * print the committed | drawn column pair (undrawn never a column). */

import { useMemo, useState } from 'react'
import { ApiError } from '../../lib/api'
import { formatNumberString, formatOfficerDate, humanizeTokensIn } from '../../lib/officerFormat'
import { bookPlug, confirmPreExisting, snoozeTask } from './api'
import { getCurrentUser } from '../../lib/auth'
import { verbRule } from '../../lib/roleVerbs'
import type { CardRow, DeckCard, DeckResponse, WhyChip } from './types'
import type { OpenDocPayload } from './RkCard'
import Sentence from '../Sentence'
import { compileSentence, type SlotValue } from '../../lib/sentences/compile'
import '../canon.css'

interface Props {
  card: DeckCard
  deck: DeckResponse
  onDone: (slimText: string) => void
  onOpenDoc: (payload: OpenDocPayload) => void
  onRefresh?: () => void
}

function errDetail(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { detail?: unknown } | undefined
    if (body && typeof body.detail === 'string') return body.detail
    return `${err.status} ${err.statusText}`
  }
  return err instanceof Error ? err.message : 'request failed'
}

/** The cause is built as an ARRAY OF PARTS, not a string: plain text parts
 * ({ t }) and highlighted figure/date parts ({ b }). A highlighted datum is
 * emphasis STYLING (rendered in a weight-only <b>), never literal « » marks
 * (founder canon 2026-09-03) — no guillemet ever reaches the rendered card.
 * (The tinted/1.08em "card datum look" is a separate ruling, 13d, pending —
 * NOT applied here.) */
type Part = { t: string } | { b: string }
const T = (t: string): Part => ({ t })
const B = (b: string): Part => ({ b })

/** A tagged template that stitches text and parts into one Part[]: a literal
 * chunk becomes a text part, an interpolated string a text part, and an
 * interpolated Part / Part[] is spliced in as-is. Lets the sentences below
 * read almost exactly as prose while emitting parts. */
type Frag = string | Part | Part[]
function seg(strings: TemplateStringsArray, ...vals: Frag[]): Part[] {
  const out: Part[] = []
  strings.forEach((str, i) => {
    if (str) out.push(T(str))
    if (i < vals.length) {
      const v = vals[i]
      if (Array.isArray(v)) out.push(...v)
      else if (typeof v === 'string') {
        if (v) out.push(T(v))
      } else out.push(v)
    }
  })
  return out
}

/** A figure as a datum inside the cause: the officer number rule (integers
 * bare, fractions at exactly two decimals, thousands grouped —
 * lib/officerFormat), a true minus sign, emitted as a highlighted { b } part.
 * The engine hands Decimal strings ("-8000.000000"); the part carries
 * "−8,000". Pre-formatted fixture text ("+6,250,000") passes through. A
 * missing figure is a plain em dash "—" (never "«—»"). */
function datumPart(v: string | null | undefined): Part {
  if (v == null) return T('—')
  return B(formatNumberString(v).replace(/^-/, '−'))
}

/** A date inside the cause, in the one officer style ("16 Feb 2026" — K7),
 * emitted as the parenthesised aside " (16 Feb 2026)" with the date itself a
 * highlighted { b } part. The engine hands ISO ("2026-02-16"); anything
 * already in words passes through. Empty when there is no date. */
function datedParts(v: string | null | undefined): Part[] {
  const d = formatOfficerDate(v)
  return d ? [T(' ('), B(d), T(')')] : []
}

/** Every cause and reason token the engine can put on a reconcile card
 * (src/nolos/recon/diagnosis.py — the four verdicts and the REASON_* set;
 * src/nolos/recon/balance_engine.py — the two leg-identity violations),
 * each as one officer sentence. Keyed by REASON first because the reason
 * is the discriminator: the cause "undeterminable" alone says nothing.
 *
 * Rule of the house (Ale 2026-09-02): no backend variable name reaches the
 * UI with "_" — sentences, not labels. The record/movements comparison is
 * part of the sentence, never a dash-suffix. */
type CauseWords = {
  balanceWord: string
  stored: Part
  computed: Part
  asOf: Part[]
  lastMove: Part[]
  lastMoveDate: string | null
}

const YOUR_CALL = 'Which side is right is your call.'

function reasonSentence(reason: string, w: CauseWords): Part[] | null {
  const both = seg`the record says ${w.stored}, the movements say ${w.computed}`
  const Both = seg`The record says ${w.stored}, the movements say ${w.computed}`
  switch (reason) {
    case 'balance_evidence_newer_than_latest_trade':
      return seg`The ${w.balanceWord}${w.asOf} is newer than the last movement${w.lastMove}: the record says ${w.stored}, the movements only add up to ${w.computed}. A movement is missing.`
    case 'latest_trade_newer_than_balance_evidence':
      return seg`A movement${w.lastMove} is newer than the ${w.balanceWord}${w.asOf}: the recorded ${w.stored} is out of date; the movements now add up to ${w.computed}.`
    case 'movements_post_date_the_stated_balance': {
      const asOfMove: Part[] = w.lastMoveDate ? [T(' as of '), B(w.lastMoveDate)] : []
      return seg`The ${w.balanceWord}${w.asOf} is older than its movements: they carry ${w.stored} forward to ${w.computed}${asOfMove}. Nothing disagrees — the statement is not wrong, only older than the movements.`
    }
    case 'balance_and_trade_same_date':
      return seg`The ${w.balanceWord} and the movements carry the same date${w.asOf}: ${both}. Neither is clearly the later one — ${YOUR_CALL.toLowerCase()}`
    case 'no_confirmed_trade_date':
      // The opening case (every reco card in the 2026-09-01 rehearsal
      // videos): a balance is on record but no confirmed movement dates it.
      return seg`The ${w.balanceWord}${w.asOf} says ${w.stored}, but no confirmed movement dates it — the movements only add up to ${w.computed}. Either a movement is missing, or the holding predates the record and the opening is booked below.`
    case 'no_balance_evidence_date':
      return seg`The ${w.balanceWord} carries no date, so it cannot be dated against the movements${w.lastMove}: ${both}. ${YOUR_CALL}`
    case 'no_balance_evidence_date_and_no_confirmed_trade_date':
      return seg`Neither the ${w.balanceWord} nor the movements carry a date to compare: ${both}. ${YOUR_CALL}`
    case 'suggested_trade_contradicts_object_sign_rule':
      return seg`${Both}. Booking the difference as a movement would leave the record in a state it may not hold, so a missing movement cannot be the explanation. ${YOUR_CALL}`
    case 'restated_balance_contradicts_object_sign_rule':
      return seg`${Both}. Restating the ${w.balanceWord} to the movements' sum would leave the record in a state it may not hold, so an out-of-date ${w.balanceWord} cannot be the whole explanation — the movement history itself is incomplete. ${YOUR_CALL}`
    case 'no_movements_after_the_stated_balance':
      return seg`${Both}. No movement is dated after the stated ${w.balanceWord}${w.asOf}, so there is nothing to roll forward.`
    case 'movements_after_the_balance_carry_no_quantity':
      return seg`${Both}. At least one movement dated after the ${w.balanceWord}${w.asOf} carries no quantity, so their sum would state a figure the documents do not support.`
    case 'derived_balance_contradicts_object_sign_rule':
      return seg`${Both}. Rolling the ${w.balanceWord} forward would leave the record in a state it may not hold, so the derived figure cannot be offered.`
    case 'not_a_balance_versus_trades_break':
      return seg`${Both}. This break compares two stored figures, not a ${w.balanceWord} against movements.`
    default:
      return null
  }
}

function causeFallbackSentence(cause: string, w: CauseWords): Part[] | null {
  switch (cause) {
    case 'missing_trade':
    case 'trade_missing':
      return reasonSentence('balance_evidence_newer_than_latest_trade', w)
    case 'stale_balance':
    case 'balance_stale':
      return reasonSentence('latest_trade_newer_than_balance_evidence', w)
    case 'balance_rollforward':
    case 'rollforward':
      return reasonSentence('movements_post_date_the_stated_balance', w)
    case 'undrawn_leg_positive':
      return seg`The undrawn leg on record is positive, which would make the undrawn amount negative: ${w.stored} committed, ${w.computed} drawn derived. Correct a leg and this closes itself.`
    case 'undrawn_exceeds_commitment':
      return seg`More is undrawn than was committed, which would make the drawn amount negative: ${w.stored} committed, ${w.computed} drawn derived. Correct a leg and this closes itself.`
    // 'undeterminable' is deliberately NOT handled here: it is the "we don't
    // know" verdict, carrying no information of its own. When a reason
    // accompanies it, that reason is the discriminator and must win (mapped
    // above, or humanized in the default branch below); when it stands
    // alone, the default branch prints the generic not-determinable line.
    default:
      return null
  }
}

/** Officer words for text the table does not know. A free-text diagnosis
 * (fixtures, older task rows) arrives in officer language already and is
 * kept; a bare machine token still reads as words, never as snake_case. */
function officerWords(text: string): string {
  const words = humanizeTokensIn(text).trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** The violet cause as parts — both dates, plain words (kills "Why?"),
 * figures/dates highlighted (weight-only <b>), never a guillemet. */
function causeSentence(card: DeckCard): Part[] {
  const why: WhyChip = card.why ?? { cause: 'undeterminable' }
  const isInstrument = card.anchor_role === 'instrument'
  const w: CauseWords = {
    balanceWord: isInstrument ? 'stated amount' : 'balance',
    stored: datumPart(why.stored),
    computed: datumPart(why.computed),
    asOf: datedParts(why.balance_as_of),
    lastMove: datedParts(why.latest_trade_date),
    lastMoveDate: formatOfficerDate(why.latest_trade_date),
  }
  let parts: Part[]
  switch (card.shape) {
    case 'supply_gap':
      parts = [
        T("The commitments on record add up to more than the instrument's stated supply"),
        ...(why.stored != null && why.computed != null
          ? seg`: ${w.computed} committed against ${w.stored} issued`
          : []),
        T(
          '. A commitment or the supply figure is wrong; nothing can book until a document says which.'
        ),
      ]
      break
    case 'leg_identity':
      parts = [
        T('The legs on record break the identity committed = drawn + undrawn'),
        ...(why.stored != null ? seg`: ${w.stored} committed` : []),
        ...(why.delta != null ? seg`, ${datumPart(why.delta)} undrawn` : []),
        ...(why.computed != null ? seg`, ${w.computed} drawn derived` : []),
        T('. Correct a leg and this closes itself.'),
      ]
      break
    default: {
      const reason = why.reason ?? ''
      const cause = why.cause ?? ''
      const known =
        reasonSentence(reason, w) ??
        reasonSentence(cause, w) ??
        causeFallbackSentence(cause, w) ??
        causeFallbackSentence(reason, w)
      if (known) {
        parts = known
      } else {
        // Unknown text: keep it as words and fold the comparison in so the
        // figures stay checkable — never "code — record says X" again.
        const base = reason || (cause && cause !== 'undeterminable' ? cause : '')
        parts = base
          ? seg`${officerWords(base).replace(/[.:;]\s*$/, '')}; the record says ${w.stored}, the movements say ${w.computed}.`
          : seg`The record says ${w.stored}, the movements say ${w.computed}. The cause is not determinable from the documents in hand — ${YOUR_CALL.toLowerCase()}`
      }
    }
  }
  // Last line of defence: no underscore token can render in a text part,
  // whatever the engine sent (figure/date parts carry no tokens).
  return parts.map((p) => ('t' in p ? T(humanizeTokensIn(p.t)) : p))
}

export default function RkRecoCard({ card, deck, onDone, onOpenDoc }: Props) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)

  const isInstrument = card.anchor_role === 'instrument'
  const commitment = !!card.commitment
  const movementRows = useMemo(
    () => (card.rows ?? []).filter((r) => r.row_id.startsWith('movement:')),
    [card.rows]
  )
  const legRows = useMemo(
    () => (card.rows ?? []).filter((r) => r.row_id.startsWith('leg:')),
    [card.rows]
  )
  const ledger = useMemo(() => card.ledger_context ?? {}, [card.ledger_context])
  const plug = card.plug ?? null
  /* Ale 2026-09-04: the §914a starting line reads "inception balance". */
  const preExisting =
    plug?.transaction_type === 'pre_existing_ownership' ||
    !!plug?.label?.includes('Inception') ||
    !!plug?.label?.includes('Pre-existing')
  // Role honesty (Tier-1 C1): the verb is rendered per the rank the
  // backend enforces TODAY (book-plug / confirm-pre-existing = any ops
  // officer, D4 2026-09-03) — a role below it sees the verb disabled with
  // the rule, never a live button that 403s.
  const approveRule = verbRule(
    preExisting ? 'reco_confirm_pre_existing' : 'reco_book_plug',
    getCurrentUser()?.role ?? null
  )
  const snoozeOnly = card.shape === 'supply_gap' || card.shape === 'leg_identity'

  /* Identity from the deck rail — holder · instrument · issuer. */
  const railName = (role: string) => deck.rail.find((n) => n.role === role)?.name ?? null
  const holder = railName('holder')
  const instrument = railName('instrument')
  const issuer = railName('issuer')

  const openChip = (r: CardRow) => {
    if (!r.evidence) return
    onOpenDoc({
      title: `${r.label}${r.date ? ` · ${r.date}` : ''}`,
      documentId: r.evidence.document_id,
      page: r.evidence.page,
      snippet: r.evidence.snippet,
      highlight: r.amount ?? undefined,
    })
  }

  /* Commitment: per-column figures; simple: the single amount column. */
  const cols = commitment ? 'minmax(0,2fr) 1fr 1fr 1fr 60px' : 'minmax(0,2fr) 1fr 1fr 60px'
  // Every figure on the card goes through the officer rule (integers bare,
  // fractions at exactly 2 decimals) — pre-formatted "+1,200" passes through.
  const fig = (v: string | null | undefined) => (v == null ? '—' : formatNumberString(v))
  const legAmount = (r: CardRow, legWanted: 'committed' | 'drawn') =>
    (r.leg ?? 'drawn') === legWanted ? fig(r.amount) : '—'
  const legSum = (legWanted: 'committed' | 'drawn') => {
    let sum = 0
    for (const r of movementRows) {
      if ((r.leg ?? 'drawn') !== legWanted) continue
      const n = Number((r.amount ?? '').replace(/[+,\s]/g, '').replace('−', '-'))
      if (Number.isFinite(n)) sum += n
    }
    // Officer figure rule: integers bare, fractions at exactly 2 decimals.
    return formatNumberString(sum.toFixed(6))
  }

  /* The ONE engine-picked proposal, as a SENTENCE: the figures ride inside
   * it as DATA (datum look), the verb and the words plain. 13c: the
   * commitment sign is in the WORD (drawdown / distribution) and the date
   * reads "dated", not "by". The muted tail was removed (Ale 2026-09-03);
   * the NEW / UPDATE chip carries what the tail used to say.
   *
   * Wave 3 D4 (Ale 2026-09-03): the reco card PLUGS or SNOOZES, nothing
   * else. The pre-existing opening is a plug like any other — "Book the
   * opening — N as of DATE"; its movement evidence stays OWED ("document
   * owed" on the right rail) and goes to Chase. The attestation box is
   * gone: waiving that evidence is the separate senior act behind the
   * existing waiver door, never typed here. */
  const proposal = useMemo((): {
    badge: string
    meaning: string
    values: Record<string, SlotValue>
    owed?: boolean
  } | null => {
    if (snoozeOnly || !plug) return null
    const rawDelta = plug.units_delta ?? null
    const by = ledger.balance_as_of ?? card.why?.balance_as_of ?? null
    if (preExisting) {
      // The opening is dated in full ("30 Jun 2026"): the diagnosis carries
      // the ISO balance date; the ledger's short "30 Jun" is the fallback.
      return {
        badge: 'NEW',
        meaning: 'reco.proposal.opening',
        values: { delta: rawDelta, date: card.why?.balance_as_of ?? ledger.balance_as_of ?? null },
        owed: true,
      }
    }
    if (card.shape === 'derived_balance') {
      const asOf = ledger.movements_to ?? card.why?.latest_trade_date ?? null
      return {
        badge: 'UPDATE',
        meaning: 'reco.proposal.rollforward',
        values: {
          subject: isInstrument ? 'stated amount' : 'balance',
          computed: rawDelta,
          as_of: asOf,
        },
      }
    }
    const d = Number(
      String(rawDelta ?? '')
        .replace(/[+,\s]/g, '')
        .replace('−', '-')
    )
    // 13c: for a commitment the direction lives in the word, and the amount
    // is stated positive; elsewhere the signed figure carries it.
    const verb = plug.transaction_type
      ? plug.transaction_type.replace(/_/g, ' ')
      : isInstrument
        ? d >= 0
          ? 'capital increase'
          : 'redemption'
        : commitment
          ? d >= 0
            ? 'drawdown'
            : 'distribution'
          : d >= 0
            ? 'acquisition'
            : 'disposal'
    if (commitment) {
      return {
        badge: 'NEW',
        meaning: 'reco.proposal.commitment',
        values: { verb, amount: { amount: Math.abs(d) }, date: by },
      }
    }
    return { badge: 'NEW', meaning: 'reco.proposal', values: { verb, delta: rawDelta, date: by } }
  }, [snoozeOnly, plug, preExisting, card.shape, card.why, ledger, isInstrument, commitment])

  const proposalSlim = proposal ? compileSentence(proposal.meaning, proposal.values).text : null

  const submitApprove = async () => {
    if (!card.task_id) return
    setBusy(true)
    setError(null)
    try {
      if (preExisting) {
        // The opening books through its own verb (it writes the
        // pre_existing_ownership trade); no reason travels — the plug's
        // owed document is the provenance.
        await confirmPreExisting(card.task_id)
      } else {
        await bookPlug(card.task_id)
      }
      onDone(`✓ ${proposalSlim ?? 'reconciled'}`)
    } catch (err) {
      setError(errDetail(err))
    } finally {
      setBusy(false)
    }
  }

  const submitSnooze = async (mode: 'next_document' | 'timer') => {
    if (!card.task_id) return
    setBusy(true)
    setError(null)
    try {
      if (mode === 'timer') {
        const until = new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10)
        await snoozeTask(card.task_id, 'timer', until)
        onDone('zz snoozed 7 days')
      } else {
        await snoozeTask(card.task_id, 'next_document')
        onDone('zz snoozed until the document lands')
      }
    } catch (err) {
      setError(errDetail(err))
    } finally {
      setBusy(false)
    }
  }

  const sym = (r?: CardRow) =>
    r && (r.leg ?? null) === 'committed' ? (
      <span className="cn-tdot c-pos" />
    ) : (
      <span className={`cn-tarr ${isInstrument ? 'ti' : 'tp'}`}>➜</span>
    )

  return (
    <div
      className="cnv"
      data-testid="reco-card"
      data-card-id={card.card_id}
      data-shape={card.shape}
    >
      <div className="cn-back" />
      <div className="cn-card">
        <div className="cn-ctit">
          <span className="k reconcile">Reconcile</span>
          {deck.document_id ? (
            <button
              type="button"
              className="cn-opensrc"
              onClick={() => onOpenDoc({ title: 'Source document', documentId: deck.document_id })}
            >
              Open sources 📄
            </button>
          ) : null}
        </div>

        <Sentence
          as="div"
          className="cn-ident sn-lead"
          data-testid="reco-identity"
          meaning={isInstrument ? 'reco.identity.instrument' : 'reco.identity'}
          values={isInstrument ? { instrument, issuer } : { instrument, holder, issuer }}
        />

        <div className="cn-qblock reconcile" data-testid="reco-cause">
          <div className="qq">
            {causeSentence(card).map((p, i) => ('b' in p ? <b key={i}>{p.b}</b> : p.t))}
          </div>
        </div>

        {legRows.length ? (
          <>
            <div className="cn-ohd" style={{ gridTemplateColumns: cols }}>
              <span>legs on record</span>
              <span />
              <span style={{ textAlign: 'right' }}>amount</span>
              <span />
            </div>
            {legRows.map((r) => (
              <div key={r.row_id} className="cn-oln" style={{ gridTemplateColumns: cols }}>
                <span>
                  <span className="cn-tdot c-pos" />
                  <span style={{ marginLeft: 6 }}>{r.label}</span>
                </span>
                <span />
                <span className="cn-mono" style={{ textAlign: 'right' }}>
                  {fig(r.amount)}
                </span>
                <span />
              </div>
            ))}
          </>
        ) : null}

        {movementRows.length || (!snoozeOnly && !legRows.length) ? (
          <>
            <div className="cn-ohd" style={{ gridTemplateColumns: cols }}>
              <span>movements</span>
              <span>date</span>
              {commitment ? (
                <>
                  <span style={{ textAlign: 'right' }}>committed</span>
                  <span style={{ textAlign: 'right' }}>drawn</span>
                </>
              ) : (
                <span style={{ textAlign: 'right' }}>amount</span>
              )}
              <span />
            </div>
            {movementRows.map((r) => (
              <div
                key={r.row_id}
                className="cn-oln"
                style={{ gridTemplateColumns: cols }}
                data-testid="reco-movement"
              >
                <span>
                  {sym(r)}
                  <span style={{ marginLeft: 6 }}>{r.label}</span>
                </span>
                <span className="cn-mono">{r.date ?? '—'}</span>
                {commitment ? (
                  <>
                    <span className="cn-mono" style={{ textAlign: 'right' }}>
                      {legAmount(r, 'committed')}
                    </span>
                    <span className="cn-mono" style={{ textAlign: 'right' }}>
                      {legAmount(r, 'drawn')}
                    </span>
                  </>
                ) : (
                  <span className="cn-mono" style={{ textAlign: 'right' }}>
                    {fig(r.amount)}
                  </span>
                )}
                <span>
                  {r.evidence ? (
                    <button type="button" className="cn-docchip" onClick={() => openChip(r)}>
                      📄
                    </button>
                  ) : null}
                </span>
              </div>
            ))}
            <div
              className="cn-oln osum"
              style={{ gridTemplateColumns: cols }}
              data-testid="reco-sum"
            >
              <span>to {ledger.movements_to ?? 'date'}</span>
              <span />
              {commitment ? (
                <>
                  <span className="cn-mono" style={{ textAlign: 'right' }}>
                    {legSum('committed')}
                  </span>
                  <span className="cn-mono" style={{ textAlign: 'right' }}>
                    {legSum('drawn')}
                  </span>
                </>
              ) : (
                <span className="cn-mono" style={{ textAlign: 'right' }}>
                  {fig(ledger.movements_sum)}
                </span>
              )}
              <span />
            </div>
            <div className="cn-ohd" style={{ gridTemplateColumns: cols }}>
              <span>{isInstrument ? 'issued amount' : 'balance'}</span>
              <span>as of</span>
              {commitment ? (
                <>
                  <span style={{ textAlign: 'right' }}>committed</span>
                  <span style={{ textAlign: 'right' }}>drawn</span>
                </>
              ) : (
                <span style={{ textAlign: 'right' }}>amount</span>
              )}
              <span />
            </div>
            {/* NEW canon (2026-08-31): the balance line is bold+highlighted
             * like the Σ row — the two compared figures stand out together. */}
            <div
              className="cn-oln osum"
              style={{ gridTemplateColumns: cols }}
              data-testid="reco-balance"
            >
              <span>
                <span className={`cn-tdot ${isInstrument ? 'c-ins' : 'c-pos'}`} />
                <span style={{ marginLeft: 6 }}>
                  {isInstrument
                    ? 'Stated share capital'
                    : commitment
                      ? 'Capital account statement'
                      : 'Recorded balance'}
                </span>
              </span>
              <span className="cn-mono">{ledger.balance_as_of ?? '—'}</span>
              {commitment ? (
                <>
                  <span className="cn-mono" style={{ textAlign: 'right' }}>
                    —
                  </span>
                  <span className="cn-mono" style={{ textAlign: 'right' }}>
                    {fig(ledger.balance)}
                  </span>
                </>
              ) : (
                <span className="cn-mono" style={{ textAlign: 'right' }}>
                  {fig(ledger.balance)}
                </span>
              )}
              <span className="miss4">✗</span>
            </div>
          </>
        ) : null}

        {proposal ? (
          <div
            className="cn-cl"
            style={{ borderTop: '1px solid var(--cn-line)' }}
            data-testid="reco-proposal"
          >
            {card.shape === 'derived_balance' ? (
              <span className={`cn-tdot ${isInstrument ? 'c-ins' : 'c-pos'}`} />
            ) : (
              <span className={`cn-tarr ${isInstrument ? 'ti' : 'tp'}`}>➜</span>
            )}
            <Sentence className="txt sn-lead" meaning={proposal.meaning} values={proposal.values} />

            <span className="right">
              {proposal.owed ? (
                <small className="cn-owed" data-testid="reco-owed">
                  document owed
                </small>
              ) : null}
              <span className={`cn-st ${proposal.badge === 'UPDATE' ? 'st-upd' : 'st-new'}`}>
                {proposal.badge}
              </span>
            </span>
          </div>
        ) : null}

        {card.evidence_note ? (
          <div className="cn-note" style={{ padding: '0 16px 4px' }}>
            {card.evidence_note}
          </div>
        ) : null}
        {error ? <div className="cn-errline">{error}</div> : null}

        <div className="cn-abar">
          {proposal ? (
            <button
              type="button"
              className={`cn-btn p reconcile${approveRule.allowed ? '' : ' blocked'}`}
              disabled={busy || !approveRule.allowed}
              title={approveRule.rule ?? undefined}
              onClick={submitApprove}
              data-testid="reco-approve"
            >
              Approve 1 record
            </button>
          ) : null}
          {proposal && !approveRule.allowed ? (
            <span className="cn-rule" data-testid="reco-verb-rule">
              {approveRule.rule}
            </span>
          ) : null}
          <button
            type="button"
            className="cn-btn"
            disabled={busy}
            onClick={() => submitSnooze('next_document')}
            data-testid="reco-snooze"
          >
            Snooze until the document lands
          </button>
          <span style={{ position: 'relative' }}>
            <button type="button" className="cn-btn" onClick={() => setMenuOpen((m) => !m)}>
              ⋯
            </button>
            {menuOpen ? (
              <button
                type="button"
                className="cn-btn"
                style={{ marginLeft: 6 }}
                disabled={busy}
                onClick={() => submitSnooze('timer')}
              >
                Snooze 7 days
              </button>
            ) : null}
          </span>
        </div>
      </div>
    </div>
  )
}
